import { useContext, useMemo } from "react";
import { Section } from "@models/ISchema";
import { ContextEDH } from "@context/ContextEDH";
import { AppContext } from "../context/EditSessionsContext";
import { usePastSessionsVisibilityStore } from "../context/usePastSessionsVisibilityStore";
import { getSectionPastSessions } from "../utils/getSectionPastSessions";
import { partitionSectionsByPast } from "../utils/partitionSectionsByPast";

export const useHiddenPastSessionIds = (sections: Section[]): Set<string> => {
  const { state } = useContext(AppContext);
  const { state: stateEDH } = useContext(ContextEDH);
  const showPast = usePastSessionsVisibilityStore(s => s.showPast);
  const showPastSessionsBySectionId = usePastSessionsVisibilityStore(
    s => s.showPastSessionsBySectionId,
  );

  const thresholdDate = state?.form?.blockingThreshold;
  const canEditPastSessions = !!stateEDH?.base?.base?.user?.abilities?.can_edit_past_sessions;

  return useMemo(() => {
    const hiddenIds = new Set<string>();

    const { visibleSections, pastSections, onlyPastSections } = partitionSectionsByPast(
      sections,
      thresholdDate,
      canEditPastSessions,
    );

    // Same effective state the section-level switch is rendered with in `SectionsList`: when
    // every section is past, the switch is forced on and disabled instead of leaving an empty
    // listing, so none of those sections count as hidden on that account.
    const sectionSwitchEffectivelyOn = onlyPastSections || showPast;

    if (!sectionSwitchEffectivelyOn) {
      pastSections.forEach(section => {
        [...(section?.sessions ?? []), ...(section?.unasignedSessions ?? [])].forEach(session =>
          hiddenIds.add(session.id),
        );
      });
    }

    visibleSections.forEach(section => {
      const { pastSessionIds, hasPastAndActiveSessions } = getSectionPastSessions(
        section,
        thresholdDate,
        canEditPastSessions,
      );

      if (hasPastAndActiveSessions && !showPastSessionsBySectionId[section.id]) {
        pastSessionIds.forEach(id => hiddenIds.add(id));
      }
    });

    return hiddenIds;
  }, [sections, thresholdDate, canEditPastSessions, showPast, showPastSessionsBySectionId]);
};
